import * as React from "react";
import * as ReactQuery from "react-query";
import { UseQueryResult } from "react-query";
import axios from "axios";

import { DataSourceAnalyticsConfiguration_QueryKey } from "./data-source-analytics-configuration-query-key-factory";
import { InitialStateMinimumRequirements_GraphQl } from "../../graphql/initial-state-requirements";
import { GraphqlWrappedData } from "../../graphql/api";
import {
  AnalyticsConfiguration,
  AnalyticsConfiguration_Graphql_Request,
} from "../../analytics/model";

interface DataSourceAnalyticsConfigurationConsumerInterface {
  data: UseQueryResult<AnalyticsConfiguration>["data"];
  status: UseQueryResult<AnalyticsConfiguration>["status"];
}

const defaultValue =
  {} as unknown as DataSourceAnalyticsConfigurationConsumerInterface;

const DataSourceAnalyticsConfigurationContext =
  React.createContext(defaultValue);

export interface InitialStateMinimumRequirements_Analytics
  extends InitialStateMinimumRequirements_GraphQl {}

export interface DataSourceAnalyticsConfigurationProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_Analytics;
  };
}
export const DataSourceAnalyticsConfigurationProvider: React.FunctionComponent<
  DataSourceAnalyticsConfigurationProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const queryKey = React.useMemo(() => {
    return DataSourceAnalyticsConfiguration_QueryKey.analyticsConfiguration();
  }, []);

  const queryFn: ReactQuery.QueryFunction<
    AnalyticsConfiguration,
    typeof queryKey
  > = async () => {
    const gqlQuery = `{
        bbnt_analytics_configuration {
          ${AnalyticsConfiguration_Graphql_Request}
        }
      }`;

    const headers = {
      "Content-Type": "application/json",
      Store: initialState.store_code,
    };

    const { data } = await axios.post<
      GraphqlWrappedData<"bbnt_analytics_configuration", AnalyticsConfiguration>
    >("/graphql", { query: gqlQuery }, { headers });

    const { data: gqlResponseData } = data || {};
    const { bbnt_analytics_configuration } = gqlResponseData || {};

    return bbnt_analytics_configuration;
  };

  const { status, data, ...result } = ReactQuery.useQuery(queryKey, queryFn, {
    refetchOnWindowFocus: false,
  });

  const value: DataSourceAnalyticsConfigurationConsumerInterface =
    React.useMemo(
      () => ({
        status,
        data,
      }),
      [status, data]
    );
  return (
    <DataSourceAnalyticsConfigurationContext.Provider value={value}>
      {props?.children || []}
    </DataSourceAnalyticsConfigurationContext.Provider>
  );
};

export const useDataSourceAnalyticsConfiguration = () => {
  const context = React.useContext(DataSourceAnalyticsConfigurationContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataSourceAnalyticsConfiguration must be used within a DataSourceAnalyticsConfigurationProvider"
    );
  }
  return context;
};
